(() => {
  const ICON_SELECTOR = "[data-hr-anim-icon]";
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const ensureLottieLibrary = () =>
    new Promise((resolve) => {
      if (window.lottie) {
        resolve(window.lottie);
        return;
      }

      const existing = document.querySelector("script[data-hr-lottie-loader]");
      if (existing) {
        existing.addEventListener("load", () => resolve(window.lottie), { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = "https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js";
      script.async = true;
      script.defer = true;
      script.setAttribute("data-hr-lottie-loader", "true");
      script.onload = () => resolve(window.lottie);
      document.head.appendChild(script);
    });

  const mountLottieIfProvided = async (node) => {
    const lottieSrc = node.getAttribute("data-hr-lottie-src");
    if (!lottieSrc || prefersReducedMotion) return;

    const lottie = await ensureLottieLibrary();
    if (!lottie) return;

    const container = document.createElement("span");
    container.className = "hr-icon-lottie";
    node.innerHTML = "";
    node.appendChild(container);

    lottie.loadAnimation({
      container,
      path: lottieSrc,
      renderer: "svg",
      loop: true,
      autoplay: true,
    });
  };

  const applyAnimationClass = (node, index) => {
    node.style.setProperty("--hr-icon-delay", `${(index % 7) * 70}ms`);
    node.classList.add("hr-icon-motion");
  };

  const initialize = () => {
    const icons = Array.from(document.querySelectorAll(ICON_SELECTOR));
    if (icons.length) {
      if (prefersReducedMotion) {
        icons.forEach((icon) => icon.classList.add("hr-icon-static"));
      } else {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const icon = entry.target;
              icon.classList.add("is-visible");
              observer.unobserve(icon);
            });
          },
          { threshold: 0.25, rootMargin: "0px 0px -12% 0px" }
        );

        icons.forEach((icon, index) => {
          applyAnimationClass(icon, index);
          mountLottieIfProvided(icon);
          observer.observe(icon);
        });
      }
    }

    const applyShopPayPill = () => {
      const paymentRoots = Array.from(document.querySelectorAll("[data-shopify='payment-button']"));
      if (!paymentRoots.length) return true;

      paymentRoots.forEach((root) => {
        root.style.borderRadius = "999px";
        root.style.overflow = "hidden";
        root.style.display = "block";

        const host = root.querySelector("shopify-accelerated-checkout");
        if (host) {
          host.style.display = "block";
          host.style.borderRadius = "999px";
          host.style.overflow = "hidden";
          host.style.clipPath = "inset(0 round 999px)";
          host.style.setProperty("--shopify-accelerated-checkout-button-border-radius", "999px");
          host.style.setProperty("--shopify-accelerated-checkout-inline-border-radius", "999px");
        }

        const shell = root.querySelector(".shopify-payment-button__button");
        if (shell) {
          shell.style.borderRadius = "999px";
          shell.style.overflow = "hidden";
        }
      });

      return true;
    };

    const stabilizeTestimonialHeight = () => {
      const sliders = Array.from(document.querySelectorAll("[id^='Slider-']"));
      if (!sliders.length) return;

      sliders.forEach((slider) => {
        const lists = Array.from(slider.querySelectorAll(".lm-fix ul"));
        if (!lists.length) return;

        lists.forEach((list) => {
          list.style.minHeight = "";
        });

        let maxHeight = 0;
        lists.forEach((list) => {
          const row = list.querySelector("[class*='slider-container-']");
          const target = row || list;
          const measured = Math.ceil(target.getBoundingClientRect().height);
          if (measured > maxHeight) maxHeight = measured;
        });

        if (maxHeight > 0) {
          lists.forEach((list) => {
            list.style.minHeight = `${maxHeight}px`;
          });
        }
      });
    };

    const bindReviewSliderControls = () => {
      const buttons = Array.from(document.querySelectorAll(".lumin-slider-button"));
      if (!buttons.length) return;

      buttons.forEach((button) => {
        if (button.dataset.hrBound === "true") return;
        button.dataset.hrBound = "true";

        button.addEventListener("click", () => {
          const controls = button.closest(".lumin-slider-buttons");
          if (!controls) return;
          const peers = Array.from(controls.querySelectorAll(".lumin-slider-button"));
          peers.forEach((peer) => {
            peer.disabled = true;
          });
          setTimeout(() => {
            peers.forEach((peer) => {
              peer.disabled = false;
            });
          }, 420);
          setTimeout(stabilizeTestimonialHeight, 220);
        });
      });
    };

    applyShopPayPill();
    setTimeout(applyShopPayPill, 180);
    setTimeout(applyShopPayPill, 700);
    setTimeout(applyShopPayPill, 1400);
    setTimeout(applyShopPayPill, 2800);

    const checkoutObserver = new MutationObserver(() => {
      applyShopPayPill();
      bindReviewSliderControls();
    });
    checkoutObserver.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => checkoutObserver.disconnect(), 10000);

    stabilizeTestimonialHeight();
    bindReviewSliderControls();
    setTimeout(stabilizeTestimonialHeight, 220);
    setTimeout(stabilizeTestimonialHeight, 650);

    const bindDirectCheckoutButton = () => {
      const controls = Array.from(document.querySelectorAll("[data-hr-direct-checkout]"));
      if (!controls.length) return;

      controls.forEach((control) => {
        if (control.dataset.hrDirectBound === "true") return;
        control.dataset.hrDirectBound = "true";

        control.addEventListener("click", async (event) => {
          if (control.getAttribute("aria-disabled") === "true") {
            event.preventDefault();
            return;
          }

          event.preventDefault();
          const form = control.closest("form");
          if (!form) return;

          const variantInput = form.querySelector("input[name='id']");
          const quantityInput = form.querySelector("input[name='quantity']");
          const variantId = variantInput ? Number(variantInput.value) : 0;
          const quantity = quantityInput ? Number(quantityInput.value) || 1 : 1;

          if (!variantId) {
            form.requestSubmit();
            return;
          }

          if ("disabled" in control) {
            control.disabled = true;
          } else {
            control.setAttribute("aria-disabled", "true");
          }
          try {
            await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({
                id: variantId,
                quantity: quantity > 0 ? quantity : 1,
              }),
            });
            window.location.href = "/checkout";
          } catch (error) {
            window.location.href = "/checkout";
          } finally {
            setTimeout(() => {
              if ("disabled" in control) {
                control.disabled = false;
              } else {
                control.setAttribute("aria-disabled", "false");
              }
            }, 1000);
          }
        });
      });
    };

    bindDirectCheckoutButton();
    setTimeout(bindDirectCheckoutButton, 320);
    window.addEventListener("resize", () => {
      setTimeout(stabilizeTestimonialHeight, 80);
    }, { passive: true });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, { once: true });
  } else {
    initialize();
  }
})();
